const GST_NUMBER_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

const HOSPITAL_NAME_FIELDS = [
  'hospital_name',
  'display_name',
  'legal_name',
  'name'
];

const HOSPITAL_ADDRESS_FIELDS = [
  'hospital_address',
  'billing_address',
  'address',
  'full_address'
];

const GST_NUMBER_FIELDS = [
  'gst_number',
  'gst_no',
  'gstin',
  'gst',
  'hospital_gst_number',
  'hospital_gst_no'
];

const GST_ENABLED_FIELDS = [
  'gst_enabled',
  'is_gst_enabled',
  'gstEnabled',
  'has_gst_enabled'
];

const PHARMACY_GST_ENABLED_FIELDS = [
  'gst_pharmacy_enabled',
  'is_gst_pharmacy_enabled',
  'gstPharmacyEnabled'
];

const ITEM_GST_RATE_FIELDS = [
  'gst_percentage',
  'gst_percent',
  'gst_rate',
  'tax_in_perc',
  'tax_percentage',
  'tax_rate'
];

const CGST_AMOUNT_FIELDS = ['cgst_amount', 'cgst'];
const SGST_AMOUNT_FIELDS = ['sgst_amount', 'sgst'];
const IGST_AMOUNT_FIELDS = ['igst_amount', 'igst'];

const roundToTwo = (value) => Math.round((Number(value) || 0) * 100) / 100;

const pickFirstValue = (source, keys) => {
  if (!source || typeof source !== 'object') {
    return '';
  }

  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }

  return '';
};

const pickFirstNumericValue = (source, keys) => {
  if (!source || typeof source !== 'object') {
    return null;
  }

  for (const key of keys) {
    const value = source?.[key];
    if (value === undefined || value === null || value === '') {
      continue;
    }

    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) {
      return numericValue;
    }
  }

  return null;
};

const sumNumericValues = (items = [], keys = []) =>
  roundToTwo(
    (Array.isArray(items) ? items : []).reduce((sum, item) => {
      const value = pickFirstNumericValue(item, keys);
      return sum + (value ?? 0);
    }, 0)
  );

export const normalizeGstNumber = (value) => String(value || '').replace(/\s+/g, '').toUpperCase().trim();

export const isValidGstNumber = (value) => GST_NUMBER_REGEX.test(normalizeGstNumber(value));

export const getStateCodeFromGst = (value) => {
  const gstNumber = normalizeGstNumber(value);
  if (!isValidGstNumber(gstNumber)) {
    return '';
  }

  return gstNumber.slice(0, 2);
};

export const getItemGstPercentage = (item) => {
  const rawValue = pickFirstValue(item, ITEM_GST_RATE_FIELDS);
  const numericValue = Number(rawValue || 0);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const HOSPITAL_PHONE_FIELDS = [
  'hospital_phone_numbers',
  'phone_number',
  'phone',
  'contact_number',
  'mobile',
  'hospital_phone',
  'tel',
  'landline'
];

const HOSPITAL_LOGO_FIELDS = ['hospital_logo', 'logo_url', 'logo'];

export const pickHospitalPhone = (source = {}) => {
  const v = pickFirstValue(source, HOSPITAL_PHONE_FIELDS);
  return v != null && String(v).trim() !== '' ? String(v).trim() : '';
};

export const pickHospitalLogo = (source = {}, fallback = '', preferredFields = []) => {
  const fields = [...new Set([...(Array.isArray(preferredFields) ? preferredFields : []), ...HOSPITAL_LOGO_FIELDS])];
  const value = pickFirstValue(source, fields);
  return value ? String(value) : fallback;
};

export const normalizeHospitalDetails = (source = {}) => {
  const gstNumber = normalizeGstNumber(pickFirstValue(source, GST_NUMBER_FIELDS));
  const explicitGstEnabledField = GST_ENABLED_FIELDS.find((key) => typeof source?.[key] === 'boolean');
  const explicitGstEnabled = explicitGstEnabledField ? source?.[explicitGstEnabledField] : undefined;
  const explicitPharmacyGstEnabledField = PHARMACY_GST_ENABLED_FIELDS.find((key) => typeof source?.[key] === 'boolean');
  const explicitPharmacyGstEnabled = explicitPharmacyGstEnabledField ? source?.[explicitPharmacyGstEnabledField] : undefined;
  const gstEnabled = typeof explicitGstEnabled === 'boolean' ? explicitGstEnabled : Boolean(gstNumber);
  const pharmacyGstEnabled = typeof explicitPharmacyGstEnabled === 'boolean' ? explicitPharmacyGstEnabled : gstEnabled;

  return {
    ...source,
    name: pickFirstValue(source, HOSPITAL_NAME_FIELDS) || 'Hospital',
    address: pickFirstValue(source, HOSPITAL_ADDRESS_FIELDS),
    gstNumber,
    gstEnabled,
    pharmacyGstEnabled,
    stateCode: getStateCodeFromGst(gstNumber)
  };
};

export const normalizeSupplierDetails = (source = {}) => {
  const gstNumber = normalizeGstNumber(pickFirstValue(source, GST_NUMBER_FIELDS));

  return {
    ...source,
    name: source?.name || source?.supplier_name || 'Supplier',
    address: source?.address || '',
    phoneNumber: source?.phone_number || source?.phone || '',
    gstNumber,
    stateCode: getStateCodeFromGst(gstNumber)
  };
};

export const getPurchaseTaxMode = ({ supplierGstNumber, hospitalGstNumber, gstEnabled = true }) => {
  if (!gstEnabled) {
    return 'none';
  }

  const supplierStateCode = getStateCodeFromGst(supplierGstNumber);
  const hospitalStateCode = getStateCodeFromGst(hospitalGstNumber);

  if (!supplierStateCode || !hospitalStateCode) {
    return 'unknown';
  }

  return supplierStateCode === hospitalStateCode ? 'cgst_sgst' : 'igst';
};

export const getExplicitGstAmounts = (source = {}) => {
  const directCgstAmount = pickFirstNumericValue(source, CGST_AMOUNT_FIELDS);
  const directSgstAmount = pickFirstNumericValue(source, SGST_AMOUNT_FIELDS);
  const directIgstAmount = pickFirstNumericValue(source, IGST_AMOUNT_FIELDS);
  const batchCgstAmount = sumNumericValues(source?.batches, CGST_AMOUNT_FIELDS);
  const batchSgstAmount = sumNumericValues(source?.batches, SGST_AMOUNT_FIELDS);
  const batchIgstAmount = sumNumericValues(source?.batches, IGST_AMOUNT_FIELDS);

  const cgstAmount = roundToTwo(
    directCgstAmount !== null && directCgstAmount !== 0 ? directCgstAmount : batchCgstAmount
  );
  const sgstAmount = roundToTwo(
    directSgstAmount !== null && directSgstAmount !== 0 ? directSgstAmount : batchSgstAmount
  );
  const igstAmount = roundToTwo(
    directIgstAmount !== null && directIgstAmount !== 0 ? directIgstAmount : batchIgstAmount
  );

  return {
    cgstAmount,
    sgstAmount,
    igstAmount,
    totalTax: roundToTwo(cgstAmount + sgstAmount + igstAmount)
  };
};

export const applyExplicitGstAmounts = (summary = {}, source = {}) => {
  const explicitAmounts = getExplicitGstAmounts(source);
  if (explicitAmounts.totalTax <= 0) {
    return summary;
  }

  const mode = explicitAmounts.igstAmount > 0
    ? 'igst'
    : (explicitAmounts.cgstAmount > 0 || explicitAmounts.sgstAmount > 0 ? 'cgst_sgst' : summary.mode);

  return {
    ...summary,
    mode,
    totalTax: explicitAmounts.totalTax,
    cgstAmount: explicitAmounts.cgstAmount,
    sgstAmount: explicitAmounts.sgstAmount,
    igstAmount: explicitAmounts.igstAmount
  };
};

export const calculatePurchaseLineGst = ({
  quantity = 0,
  unitPrice = 0,
  discountPercentage = 0,
  taxPercentage = 0,
  supplierGstNumber = '',
  hospitalGstNumber = '',
  gstEnabled = true,
  mode = null,
}) => {
  const safeQuantity = Number(quantity || 0);
  const safeUnitPrice = Number(unitPrice || 0);
  const safeDiscountPercentage = Number(discountPercentage || 0);
  const safeTaxPercentage = Number(taxPercentage || 0);

  const subtotalBeforeDiscount = roundToTwo(safeQuantity * safeUnitPrice);
  const discountAmount = roundToTwo((subtotalBeforeDiscount * safeDiscountPercentage) / 100);
  const taxableValue = roundToTwo(subtotalBeforeDiscount - discountAmount);
  const totalTax = gstEnabled ? roundToTwo((taxableValue * safeTaxPercentage) / 100) : 0;
  
  const effectiveMode = mode || getPurchaseTaxMode({
    supplierGstNumber,
    hospitalGstNumber,
    gstEnabled
  });

  let cgstAmount = 0;
  let sgstAmount = 0;
  let igstAmount = 0;
  let cgstPercentage = 0;
  let sgstPercentage = 0;
  let igstPercentage = 0;

  console.log(effectiveMode)
  if (gstEnabled && totalTax > 0) {
    if (effectiveMode === 'igst') {
      igstAmount = totalTax;
      igstPercentage = safeTaxPercentage;
    } else if (effectiveMode === 'cgst_sgst') {
      cgstAmount = roundToTwo(totalTax / 2);
      sgstAmount = roundToTwo(totalTax - cgstAmount);
      cgstPercentage = safeTaxPercentage / 2;
      sgstPercentage = safeTaxPercentage / 2;
    }
  }

  return {
    mode: effectiveMode,
    subtotalBeforeDiscount,
    discountAmount,
    taxableValue,
    totalTax,
    cgstAmount,
    sgstAmount,
    igstAmount,
    cgstPercentage,
    sgstPercentage,
    igstPercentage,
    totalAmount: roundToTwo(taxableValue + totalTax)
  };
};

export const calculateInclusiveLineGst = ({
  grossUnitPrice = 0,
  quantity = 0,
  discountPercentage = 0,
  taxPercentage = 0,
  gstEnabled = true,
  mode = 'cgst_sgst',
  explicitCgstAmount: explicitCgstAmountInput = null,
  explicitSgstAmount: explicitSgstAmountInput = null,
  explicitIgstAmount: explicitIgstAmountInput = null
}) => {
  const safeQuantity = Number(quantity || 0);
  const safeGrossUnitPrice = Number(grossUnitPrice || 0);
  const safeDiscountPercentage = Number(discountPercentage || 0);
  const safeTaxPercentage = Number(taxPercentage || 0);

  const grossBeforeDiscount = roundToTwo(safeGrossUnitPrice * safeQuantity);
  const discountAmount = roundToTwo((grossBeforeDiscount * safeDiscountPercentage) / 100);
  const grossAfterDiscount = roundToTwo(grossBeforeDiscount - discountAmount);
  const explicitCgstAmount = roundToTwo(Number(explicitCgstAmountInput ?? 0));
  const explicitSgstAmount = roundToTwo(Number(explicitSgstAmountInput ?? 0));
  const explicitIgstAmount = roundToTwo(Number(explicitIgstAmountInput ?? 0));
  const explicitTotalTax = roundToTwo(explicitCgstAmount + explicitSgstAmount + explicitIgstAmount);

  if (!gstEnabled || grossAfterDiscount <= 0) {
    return {
      mode: 'none',
      grossBeforeDiscount,
      discountAmount,
      grossAfterDiscount,
      taxableValue: grossAfterDiscount,
      totalTax: 0,
      cgstAmount: 0,
      sgstAmount: 0,
      igstAmount: 0,
      cgstPercentage: 0,
      sgstPercentage: 0,
      igstPercentage: 0,
      totalAmount: grossAfterDiscount
    };
  }

  if (explicitTotalTax > 0) {
    const explicitMode = explicitIgstAmount > 0 ? 'igst' : 'cgst_sgst';

    return {
      mode: explicitMode,
      grossBeforeDiscount,
      discountAmount,
      grossAfterDiscount,
      taxableValue: roundToTwo(grossAfterDiscount - explicitTotalTax),
      totalTax: explicitTotalTax,
      cgstAmount: explicitCgstAmount,
      sgstAmount: explicitSgstAmount,
      igstAmount: explicitIgstAmount,
      cgstPercentage: explicitMode === 'cgst_sgst' ? safeTaxPercentage / 2 : 0,
      sgstPercentage: explicitMode === 'cgst_sgst' ? safeTaxPercentage / 2 : 0,
      igstPercentage: explicitMode === 'igst' ? safeTaxPercentage : 0,
      totalAmount: grossAfterDiscount
    };
  }

  if (safeTaxPercentage <= 0) {
    return {
      mode: 'none',
      grossBeforeDiscount,
      discountAmount,
      grossAfterDiscount,
      taxableValue: grossAfterDiscount,
      totalTax: 0,
      cgstAmount: 0,
      sgstAmount: 0,
      igstAmount: 0,
      cgstPercentage: 0,
      sgstPercentage: 0,
      igstPercentage: 0,
      totalAmount: grossAfterDiscount
    };
  }

  const taxDivisor = 1 + (safeTaxPercentage / 100);
  const taxableValue = roundToTwo(grossAfterDiscount / taxDivisor);
  const totalTax = roundToTwo(grossAfterDiscount - taxableValue);

  let cgstAmount = 0;
  let sgstAmount = 0;
  let igstAmount = 0;
  let cgstPercentage = 0;
  let sgstPercentage = 0;
  let igstPercentage = 0;

  if (mode === 'igst') {
    igstAmount = totalTax;
    igstPercentage = safeTaxPercentage;
  } else {
    cgstAmount = roundToTwo(totalTax / 2);
    sgstAmount = roundToTwo(totalTax - cgstAmount);
    cgstPercentage = safeTaxPercentage / 2;
    sgstPercentage = safeTaxPercentage / 2;
  }

  return {
    mode,
    grossBeforeDiscount,
    discountAmount,
    grossAfterDiscount,
    taxableValue,
    totalTax,
    cgstAmount,
    sgstAmount,
    igstAmount,
    cgstPercentage,
    sgstPercentage,
    igstPercentage,
    totalAmount: grossAfterDiscount
  };
};

export const summarizePurchaseBillGst = ({
  items = [],
  supplierGstNumber = '',
  hospitalGstNumber = '',
  gstEnabled = true,
  summarySource = null
}) => {

  const globalMode = items.some(item => Number(item?.igst) > 0)
  ? 'igst'
  : items.some(item => Number(item?.cgst) > 0 || Number(item?.sgst) > 0)
    ? 'cgst_sgst'
    : getPurchaseTaxMode({ supplierGstNumber, hospitalGstNumber, gstEnabled });

  const summary = items.reduce(
    (summary, item) => {
      const line = calculatePurchaseLineGst({
        quantity: item?.quantity,
        unitPrice: item?.buying_price,
        discountPercentage: item?.discount_in_perc,
        taxPercentage: item?.tax_in_perc,
        supplierGstNumber,
        hospitalGstNumber,
        gstEnabled,
        mode: globalMode,
      });

      return {
        mode: line.mode,
        subtotalBeforeDiscount: roundToTwo(summary.subtotalBeforeDiscount + line.subtotalBeforeDiscount),
        discountAmount: roundToTwo(summary.discountAmount + line.discountAmount),
        taxableValue: roundToTwo(summary.taxableValue + line.taxableValue),
        totalTax: roundToTwo(summary.totalTax + line.totalTax),
        cgstAmount: roundToTwo(summary.cgstAmount + line.cgstAmount),
        sgstAmount: roundToTwo(summary.sgstAmount + line.sgstAmount),
        igstAmount: roundToTwo(summary.igstAmount + line.igstAmount),
        totalAmount: roundToTwo(summary.totalAmount + line.totalAmount)
      };
    },
    {
      mode: getPurchaseTaxMode({ supplierGstNumber, hospitalGstNumber, gstEnabled }),
      subtotalBeforeDiscount: 0,
      discountAmount: 0,
      taxableValue: 0,
      totalTax: 0,
      cgstAmount: 0,
      sgstAmount: 0,
      igstAmount: 0,
      totalAmount: 0
    }
  );

  return summarySource ? applyExplicitGstAmounts(summary, summarySource) : summary;
};

export const summarizeInclusiveGst = ({ items = [], gstEnabled = true, mode = 'cgst_sgst', summarySource = null }) => {
  const summary = items.reduce(
    (summary, item) => {
      const explicitAmounts = getExplicitGstAmounts(item);
      const line = calculateInclusiveLineGst({
        grossUnitPrice: item?.selling_price,
        quantity: item?.quantity ?? item?.qty,
        discountPercentage: item?.discount_in_perc,
        taxPercentage: getItemGstPercentage(item),
        gstEnabled,
        mode,
        explicitCgstAmount: explicitAmounts.cgstAmount,
        explicitSgstAmount: explicitAmounts.sgstAmount,
        explicitIgstAmount: explicitAmounts.igstAmount
      });

      return {
        mode: line.mode,
        grossBeforeDiscount: roundToTwo(summary.grossBeforeDiscount + line.grossBeforeDiscount),
        discountAmount: roundToTwo(summary.discountAmount + line.discountAmount),
        grossAfterDiscount: roundToTwo(summary.grossAfterDiscount + line.grossAfterDiscount),
        taxableValue: roundToTwo(summary.taxableValue + line.taxableValue),
        totalTax: roundToTwo(summary.totalTax + line.totalTax),
        cgstAmount: roundToTwo(summary.cgstAmount + line.cgstAmount),
        sgstAmount: roundToTwo(summary.sgstAmount + line.sgstAmount),
        igstAmount: roundToTwo(summary.igstAmount + line.igstAmount),
        totalAmount: roundToTwo(summary.totalAmount + line.totalAmount)
      };
    },
    {
      mode: gstEnabled ? mode : 'none',
      grossBeforeDiscount: 0,
      discountAmount: 0,
      grossAfterDiscount: 0,
      taxableValue: 0,
      totalTax: 0,
      cgstAmount: 0,
      sgstAmount: 0,
      igstAmount: 0,
      totalAmount: 0
    }
  );

  return summarySource ? applyExplicitGstAmounts(summary, summarySource) : summary;
};

export const formatGstModeLabel = (mode) => {
  if (mode === 'igst') {
    return 'IGST';
  }

  if (mode === 'cgst_sgst') {
    return 'CGST + SGST';
  }

  if (mode === 'unknown') {
    return 'GST';
  }

  return 'No GST';
};
